import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { api, API_BASE } from "@/lib/api";
import { Ticket, Anchor, ChevronRight, Share2, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { PORTAL } from "@/constants/testIds";

const BASE = API_BASE.replace(/\/api$/, "");

export default function ContestSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [portal, setPortal] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let stop = false;
    const poll = async (n) => {
      if (stop || n > 12) return;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        setAttempts(n);
        if (data.payment_status === "paid") {
          const portalRes = await api.get(`/applications/portal/${sessionId}`);
          setPortal(portalRes.data);
          setStatus("paid");
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        setTimeout(() => poll(n + 1), 2000);
      } catch (_) {
        setTimeout(() => poll(n + 1), 2500);
      }
    };
    poll(1);
    return () => { stop = true; };
  }, [sessionId]);

  const shareUrl = portal ? `${window.location.origin}/candidates/${portal.id}` : "";
  const copy = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true); toast.success("Share link copied"); setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Layout>
      <section className="relative min-h-[80vh] py-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-4xl mx-auto">
          {status === "polling" && (
            <div className="text-center py-20">
              <Anchor className="text-[#C9A227] mx-auto mb-6 animate-pulse" size={36}/>
              <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tighter text-white">Verifying Boarding…</h1>
              <p className="text-white/60 mt-4">Confirming your $99 deposit with Stripe. Attempt {attempts}/12.</p>
            </div>
          )}

          {status === "expired" && (
            <div className="text-center py-20">
              <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tighter text-white">Session Expired</h1>
              <p className="text-white/60 mt-4">Your boarding session expired. Reapply from the home page.</p>
              <Link to="/" className="btn-gold inline-block mt-8 text-xs">Re-board</Link>
            </div>
          )}

          {status === "error" && (
            <div className="text-center py-20">
              <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tighter text-white">Missing Session</h1>
              <p className="text-white/60 mt-4">No Stripe session detected.</p>
              <Link to="/" className="btn-gold inline-block mt-8 text-xs">Return Home</Link>
            </div>
          )}

          {status === "paid" && portal && (
            <div className="space-y-12 fade-up">
              <div className="text-center">
                <CheckCircle2 className="text-[#C9A227] mx-auto" size={48}/>
                <span className="label-gold mt-4 inline-block">Boarding Confirmed</span>
                <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tighter text-white mt-3">
                  Welcome aboard, <span className="gold-shimmer">{portal.full_name}</span>.
                </h1>
                <p className="text-white/60 mt-4 max-w-xl mx-auto">
                  Your application is locked into the Command Roster. The Grand Sovereign reviews every dossier personally.
                </p>
              </div>

              {/* Sovereign Raffle Ticket */}
              <div className="relative ticket p-8 md:p-10" data-testid={PORTAL.ticket}>
                <div className="grid md:grid-cols-[2fr_1fr] gap-8 items-center">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Ticket className="text-[#C9A227]"/>
                      <span className="label-gold">Sovereign Raffle Ticket</span>
                    </div>
                    <div className="text-4xl md:text-6xl font-black gold-shimmer tabular-nums tracking-tighter" data-testid={PORTAL.ticketNumber}>
                      {portal.raffle_ticket}
                    </div>
                    <p className="text-white/55 text-sm mt-4 leading-relaxed">
                      Your seat at the Armada Raffle table. The Grand Drawing distributes prizes valued at over $9,000 — regardless of the final Admiral selection.
                    </p>
                  </div>
                  <div className="border-l border-dashed border-[#C9A227]/40 pl-8 hidden md:block">
                    <div className="text-[9px] tracking-[0.3em] uppercase text-white/40">Holder</div>
                    <div className="text-white font-bold mt-1">{portal.full_name}</div>
                    <div className="text-[9px] tracking-[0.3em] uppercase text-white/40 mt-4">Niche</div>
                    <div className="text-white/80 text-sm mt-1">{portal.niche}</div>
                    <div className="text-[9px] tracking-[0.3em] uppercase text-white/40 mt-4">Status</div>
                    <div className="text-[#C9A227] text-sm font-bold mt-1">{portal.status?.toUpperCase()}</div>
                  </div>
                </div>
              </div>

              {/* Share to drive votes */}
              <div className="glass p-6 md:p-8 border border-[#C9A227]/20">
                <div className="flex items-center gap-3 mb-3">
                  <Share2 className="text-[#C9A227]"/>
                  <span className="label-gold">Activate the Viral Strike</span>
                </div>
                <h3 className="text-white text-xl font-black uppercase tracking-tight">Share your candidate profile.</h3>
                <p className="text-white/55 text-sm mt-2">The most-upvoted candidate seizes <span className="text-[#C9A227] font-bold">$250 + $500 ad spend</span>.</p>
                <div className="flex items-center gap-3 mt-5">
                  <input className="siren-input flex-1" value={shareUrl} readOnly data-testid={PORTAL.shareLink}/>
                  <button onClick={copy} className="btn-gold text-xs whitespace-nowrap">
                    {copied ? "Copied" : "Copy"} <Copy size={12} className="inline -mt-0.5"/>
                  </button>
                </div>
                <Link to={`/candidates/${portal.id}`} className="btn-outline-gold inline-block mt-4 text-xs">
                  View Public Profile <ChevronRight size={12} className="inline -mt-0.5"/>
                </Link>
              </div>

              {/* Portfolio */}
              {(portal.portfolio || []).length > 0 && (
                <div>
                  <span className="label-gold">Your Aesthetic Vault</span>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mt-4">
                    {portal.portfolio.map((p, i) => (
                      <div key={p.id || i} className="aspect-square overflow-hidden border border-[#C9A227]/20">
                        <img src={`${BASE}${p.url}`} alt="" className="w-full h-full object-cover"/>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
